import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Mail, MessageSquare, Image, Mic, Paperclip, Send } from 'lucide-react'
import { api } from '../utils/api'

export default function NewMessage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [type, setType] = useState<'email' | 'sms' | 'mms'>('email')
  const [recipients, setRecipients] = useState<string[]>([])
  const [recipientInput, setRecipientInput] = useState('')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [scheduleType, setScheduleType] = useState<'onetime' | 'recurring' | 'beat'>('onetime')
  const [scheduledAt, setScheduledAt] = useState('')
  const [recurringPattern, setRecurringPattern] = useState<'daily' | 'weekly' | 'monthly'>('daily')
  const [recurringStart, setRecurringStart] = useState('')
  const [recurringEnd, setRecurringEnd] = useState('')
  const [beatIntervalHours, setBeatIntervalHours] = useState(24)
  const [attachments, setAttachments] = useState<string[]>([])
  const [audioUrl, setAudioUrl] = useState('')
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  const { data: contacts } = useQuery({
    queryKey: ['contacts'],
    queryFn: api.contacts.list,
  })
  
  const createMutation = useMutation({
    mutationFn: api.messages.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages'] })
      queryClient.invalidateQueries({ queryKey: ['beatMessages'] })
      navigate('/messages')
    },
    onError: (error: Error) => {
      setError(error.message)
    },
  })
  
  const addRecipient = () => {
    if (recipientInput.trim() && !recipients.includes(recipientInput.trim())) {
      setRecipients([...recipients, recipientInput.trim()])
      setRecipientInput('')
    }
  }
  
  const removeRecipient = (r: string) => {
    setRecipients(recipients.filter(x => x !== r))
  }
  
  useEffect(() => {
    setRecipients(prev => prev.filter(r => type === 'email' ? r.includes('@') : !r.includes('@')))
  }, [type])

  const addFromContact = (contact: any) => {
    if (type === 'email') {
      if (!contact.email) {
        setError(`No email address found for ${contact.name || 'this contact'}. Please update the contact information.`)
        return
      }
      if (!recipients.includes(contact.email)) {
        setRecipients([...recipients, contact.email])
      }
    } else {
      if (!contact.phone) {
        setError(`No phone number found for ${contact.name || 'this contact'}. Please update the contact information.`)
        return
      }
      if (!recipients.includes(contact.phone)) {
        setRecipients([...recipients, contact.phone])
      }
    }
  }
  
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    
    if (file.size > 10 * 1024 * 1024) {
      alert('File too large (max 10MB)')
      return
    }
    
    setUploading(true)
    try {
      const result = await api.upload.file(file)
      setAttachments([...attachments, result.key])
    } catch (err) {
      alert('Upload failed')
    } finally {
      setUploading(false)
    }
  }
  
  const handleAudioUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    
    setUploading(true)
    try {
      const result = await api.upload.audio(file)
      setAudioUrl(result.key)
    } catch (err) {
      alert('Upload failed')
    } finally {
      setUploading(false)
    }
  }
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!recipients.length) {
      alert('Please add at least one recipient')
      return
    }
    
    if (!body.trim()) {
      alert('Please enter a message')
      return
    }
    
    if (scheduleType === 'onetime' && !scheduledAt) {
      alert('Please select a date and time')
      return
    }
    
    const toUtc = (local: string) => local ? new Date(local).toISOString() : null

    createMutation.mutate({
      type,
      recipients,
      subject: type === 'email' ? subject : undefined,
      body,
      attachments: attachments.length ? attachments : undefined,
      audioUrl: audioUrl || undefined,
      scheduleType,
      scheduledAt: scheduleType === 'onetime' ? toUtc(scheduledAt) : null,
      recurringPattern: scheduleType === 'recurring' ? recurringPattern : null,
      recurringStart: scheduleType === 'recurring' ? toUtc(recurringStart) : null,
      recurringEnd: scheduleType === 'recurring' ? toUtc(recurringEnd) : null,
      beatIntervalHours: scheduleType === 'beat' ? beatIntervalHours : null,
    })
  }
  
  return (
    <div className="max-w-3xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Create New Message</h1>
      
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center gap-2">
          <Mail className="w-5 h-5" />
          <span className="text-sm">{error}</span>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Message Type</h2>
          <div className="flex gap-3">
            {
              [
                { id: 'email', label: 'Email', icon: Mail },
                { id: 'sms', label: 'SMS', icon: MessageSquare },
                { id: 'mms', label: 'MMS', icon: Image },
              ].map(({ id, label, icon: Icon }) => (
<button
                   key={id}
                  type="button"
                   onClick={() => setType(id as any)}
                   className={`flex-1 flex items-center justify-center gap-2 p-4 rounded-lg border-2 transition-all ${
                     type === id
                       ? 'border-blue-500 bg-blue-50 text-blue-600'
                       : 'border-gray-200 hover:border-gray-300'
                   }`}
                 >
                   <Icon className="w-5 h-5" />
                   {label}
                 </button>
              ))
            }
          </div>
        </div>
        
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Recipients</h2>
          
          {contacts && contacts.length > 0 && (
            <div className="mb-4">
              <p className="text-sm text-gray-500 mb-2">Quick add from contacts:</p>
              <div className="flex flex-wrap gap-2">
                {contacts.map((c: any) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => addFromContact(c)}
                    className="px-3 py-1 bg-gray-100 rounded-full text-sm hover:bg-gray-200"
                  >
                    {c.name || c.email || c.phone}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              value={recipientInput}
              onChange={(e) => setRecipientInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addRecipient())}
              placeholder={type === 'email' ? 'email@example.com' : '+1234567890'}
              className="input"
            />
            <button type="button" onClick={addRecipient} className="btn btn-secondary">
              Add
            </button>
          </div>
          
          {recipients.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {recipients.map((r) => (
                <span key={r} className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full">
                  {r}
                  <button type="button" onClick={() => removeRecipient(r)} className="hover:text-blue-900">×</button>
                </span>
              ))}
            </div>
          )}
        </div>
        
        {type === 'email' && (
          <div className="card">
            <h2 className="text-lg font-semibold mb-4">Subject</h2>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter subject..."
              className="input"
            />
          </div>
        )}
        
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Message Body</h2>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Type your message..."
            rows={6}
            className="input"
          />
          
          <div className="mt-4 flex gap-3">
            <label className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg cursor-pointer hover:bg-gray-200">
              <Paperclip className="w-5 h-5" />
              <span className="text-sm">Attach File</span>
              <input type="file" className="hidden" onChange={handleFileUpload} accept=".pdf,.doc,.docx,.txt" />
            </label>
            
            <label className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg cursor-pointer hover:bg-gray-200">
              <Mic className="w-5 h-5" />
              <span className="text-sm">Audio</span>
              <input type="file" className="hidden" accept="audio/*" onChange={handleAudioUpload} />
            </label>
            
            {attachments.map((_, i) => (
              <span key={i} className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm">
                File {i + 1}
              </span>
            ))}
            
            {audioUrl && (
              <span className="px-3 py-2 bg-green-100 text-green-700 rounded-lg text-sm">
                Audio attached
              </span>
            )}
          </div>
          
          {uploading && <p className="text-sm text-gray-500 mt-2">Uploading...</p>}
        </div>
        
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Schedule</h2>
          <div className="flex gap-3 mb-4">
            {
              [
                { id: 'onetime', label: 'One Time' },
                { id: 'recurring', label: 'Recurring' },
                { id: 'beat', label: 'Beat Sending' },
              ].map(({ id, label }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setScheduleType(id as any)}
                  className={`flex-1 p-3 rounded-lg border-2 text-center ${
                    scheduleType === id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {label}
                </button>
              ))
            }
          </div>
          
          {scheduleType === 'onetime' && (
            <input
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              className="input"
            />
          )}
          
          {scheduleType === 'recurring' && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Start Date & Time</label>
                <input
                  type="datetime-local"
                  value={recurringStart}
                  onChange={(e) => setRecurringStart(e.target.value)}
                  className="input"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Repeat</label>
                <select
                  value={recurringPattern}
                  onChange={(e) => setRecurringPattern(e.target.value as any)}
                  className="input"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">End Date (optional)</label>
                <input
                  type="datetime-local"
                  value={recurringEnd}
                  onChange={(e) => setRecurringEnd(e.target.value)}
                  className="input"
                />
              </div>
            </div>
          )}
          
          {scheduleType === 'beat' && (
            <div className="space-y-3">
              <label className="block text-sm text-gray-600">
                Send if you don't punch in for:
              </label>
              <select
                value={beatIntervalHours}
                onChange={(e) => setBeatIntervalHours(Number(e.target.value))}
                className="input"
              >
                <option value={6}>6 hours</option>
                <option value={12}>12 hours</option>
                <option value={24}>24 hours (default)</option>
                <option value={36}>36 hours</option>
                <option value={48}>48 hours</option>
                <option value={72}>72 hours</option>
              </select>
            </div>
          )}
        </div>
        
        <div className="flex justify-end gap-3">
          <button type="button" onClick={() => navigate('/messages')} className="btn btn-secondary">
            Cancel
          </button>
          <button type="submit" disabled={createMutation.isPending} className="btn btn-primary flex items-center gap-2">
            <Send className="w-5 h-5" />
            {createMutation.isPending ? 'Creating...' : 'Create Message'}
          </button>
        </div>
      </form>
    </div>
  )
}